/**
 * Where a pathway edit lock's tab signature is kept, and why it is kept twice.
 *
 * The server mints the signature once, hands it to the tab that asked, and refuses any
 * write that cannot quote it back. The ticket asks that copying the pathway URL into a
 * new tab must not carry the lock with it — so the signature lives in **two** places and
 * neither alone is the credential:
 *
 *   - the URL (`?lockToken=…`), so a reload keeps the lock rather than stranding it
 *   - `sessionStorage`, which a new tab does not inherit even when the URL is pasted in
 *
 * Both must agree. Paste the URL into a second tab and the URL has a signature that
 * sessionStorage has never seen: we treat that tab as holding nothing, tell the author
 * their pathway is open elsewhere, and offer to move the lock here.
 *
 * sessionStorage rather than localStorage on purpose. localStorage is shared by every tab
 * on the origin, which is exactly the thing being distinguished — two tabs of the same
 * person — so storing it there would hand the second tab the credential and defeat the
 * guard. sessionStorage is per tab, survives a reload, and dies with the tab.
 */

const STORAGE_PREFIX = 'pathwayEditLockToken'
export const LOCK_TOKEN_PARAM = 'lockToken'

const storageKey = (contentParentId: string) => `${STORAGE_PREFIX}:${contentParentId}`

/** Storage throws in private modes and sandboxed frames; a lock is never worth a crash. */
const safely = <T>(fn: () => T, fallback: T): T => {
  try {
    return fn()
  } catch {
    return fallback
  }
}

export const readStoredToken = (contentParentId: string): string | null =>
  safely(() => window.sessionStorage.getItem(storageKey(contentParentId)), null)

export const writeStoredToken = (contentParentId: string, token: string) =>
  safely(() => window.sessionStorage.setItem(storageKey(contentParentId), token), undefined)

export const clearStoredToken = (contentParentId: string) =>
  safely(() => window.sessionStorage.removeItem(storageKey(contentParentId)), undefined)

export const readUrlToken = (): string | null =>
  safely(() => new URLSearchParams(window.location.search).get(LOCK_TOKEN_PARAM), null)

/**
 * Put the signature in the address bar without adding a history entry — the author did
 * not navigate, and a Back button that steps through lock states would be nonsense.
 */
export const writeUrlToken = (token: string) =>
  safely(() => {
    const url = new URL(window.location.href)
    if (url.searchParams.get(LOCK_TOKEN_PARAM) === token) return
    url.searchParams.set(LOCK_TOKEN_PARAM, token)
    window.history.replaceState(window.history.state, '', url.toString())
  }, undefined)

export const clearUrlToken = () =>
  safely(() => {
    const url = new URL(window.location.href)
    if (!url.searchParams.has(LOCK_TOKEN_PARAM)) return
    url.searchParams.delete(LOCK_TOKEN_PARAM)
    window.history.replaceState(window.history.state, '', url.toString())
  }, undefined)

/**
 * The signature this tab may actually use: present in the URL *and* matching what this
 * tab stored. A URL alone is a pasted link; storage alone is a lock whose URL was edited.
 */
export const readTabToken = (contentParentId: string): string | null => {
  const stored = readStoredToken(contentParentId)
  if (!stored) return null
  return readUrlToken() === stored ? stored : null
}

/**
 * The signature this tab holds, whichever pathway it belongs to.
 *
 * `readTabToken` answers "may this tab write to pathway N", which needs the id. The
 * Apollo link has no reliable id: a pathway is changed by something like seventy
 * mutations, and plenty of them name a child record instead — `updateNodeComment` takes
 * a comment id, `updateWorkflowNode` a node id, `toggleShowText` neither. Deriving the
 * pathway from the variables therefore misses, no header goes out, and the server refuses
 * the *holder's own* write. That is a denial of service on the feature's happy path, and
 * it is the person who took the lock who hits it.
 *
 * So the link sends whatever this tab holds and lets the server decide. The asymmetry is
 * the whole argument: sending a signature on an operation that turns out to be about
 * another pathway is inert — the server ignores the header unless a lock exists on the
 * content being written, and a signature from a different lock will not match one that
 * does — while failing to send it breaks editing outright.
 *
 * Both halves are still required, so a pasted URL yields nothing here either.
 */
export const readHeldToken = (): string | null => {
  const fromUrl = readUrlToken()
  if (!fromUrl) return null

  return safely(() => {
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i)
      if (!key?.startsWith(`${STORAGE_PREFIX}:`)) continue
      if (window.sessionStorage.getItem(key) === fromUrl) return fromUrl
    }
    return null
  }, null)
}

/**
 * True when the URL carries a signature this tab cannot match — the pasted-link case the
 * ticket asks us to warn about, and the one case where "you already have this open" is
 * worth saying out loud.
 */
export const hasForeignUrlToken = (contentParentId: string): boolean => {
  const fromUrl = readUrlToken()
  return Boolean(fromUrl) && fromUrl !== readStoredToken(contentParentId)
}

/**
 * Put the stored half back in the address bar if something removed it.
 *
 * The URL is not ours alone. PrintModal rewrites it to `protocol + host + pathname` when
 * a publish or offline copy finishes, dropping the whole query string and `lockToken`
 * with it -- and that happens inside the editor, to the holder, on the pathway they are
 * holding. After it the tab still holds the lock server-side but can no longer prove it:
 * no header, writes refused, and nothing puts the signature back, because a signature is
 * only ever minted once and the poll response does not carry one.
 *
 * Restoring from *storage* rather than from the server is what keeps this safe. Only a
 * tab that was told the signature has it stored; a tab holding a pasted URL has nothing
 * to restore from, so this can never hand it the credential it is missing.
 */
export const restoreUrlToken = (contentParentId: string) => {
  const stored = readStoredToken(contentParentId)
  if (stored) writeUrlToken(stored)
}

export const rememberToken = (contentParentId: string, token: string) => {
  writeStoredToken(contentParentId, token)
  writeUrlToken(token)
}

export const forgetToken = (contentParentId: string) => {
  clearStoredToken(contentParentId)
  clearUrlToken()
}
